import {
  loadSettings, saveSettings, exportAllData, resetAllData,
  type Settings,
} from '../data/stores.ts';
import { CEFR_ORDER } from '../data/candos.ts';
import { speechCapabilities } from '../data/speech.ts';
import { loadTtsManifest, ttsCoverage } from '../data/tts-loader.ts';

export function renderParametres(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col gap-6 max-w-2xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12 text-center">Chargement…</p>';
  init(root);
  return root;
}

async function init(root: HTMLElement): Promise<void> {
  const [settings, caps, ttsManifest] = await Promise.all([
    loadSettings(),
    speechCapabilities(),
    loadTtsManifest(),
  ]);
  root.innerHTML = '';
  root.append(renderHeader());
  root.append(renderLearningSection(settings));
  root.append(renderDailyLimitsSection(settings));
  root.append(renderAppearanceSection(settings));
  root.append(renderAudioSection(caps, ttsCoverage(ttsManifest), ttsManifest.voices.length));
  root.append(renderDataSection());
}

function renderHeader(): HTMLElement {
  const h = document.createElement('header');
  h.innerHTML = `
    <h1 class="text-3xl font-semibold tracking-tight">Paramètres</h1>
    <p class="text-sm text-[color:var(--color-ink-muted)] mt-1">Vos préférences sont enregistrées localement (IndexedDB).</p>
  `;
  return h;
}

function section(title: string): HTMLElement {
  const s = document.createElement('section');
  s.className = 'rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6';
  s.innerHTML = `<h2 class="text-lg font-semibold mb-4">${title}</h2>`;
  return s;
}

function renderLearningSection(settings: Settings): HTMLElement {
  const sec = section('Apprentissage');
  const wrap = document.createElement('div');
  wrap.className = 'grid grid-cols-1 sm:grid-cols-2 gap-4';

  // Current level
  const levelField = field('Niveau actuel', 'Filtre le contenu présenté dans les pistes.');
  const levelSelect = document.createElement('select');
  levelSelect.className = inputClasses();
  for (const level of CEFR_ORDER) {
    const opt = document.createElement('option');
    opt.value = level;
    opt.textContent = level;
    if (level === settings.currentLevel) opt.selected = true;
    levelSelect.append(opt);
  }
  levelSelect.addEventListener('change', () => {
    settings.currentLevel = levelSelect.value as Settings['currentLevel'];
    void saveSettings(settings);
  });
  levelField.append(levelSelect);
  wrap.append(levelField);

  // Dialect
  const dialectField = field('Dialecte d’audition', 'Choix par défaut pour la piste Écoute.');
  const dialectSelect = document.createElement('select');
  dialectSelect.className = inputClasses();
  for (const [val, label] of [['bokmal', 'Bokmål standard'], ['siddis', 'Siddis (Stavanger)']] as const) {
    const opt = document.createElement('option');
    opt.value = val;
    opt.textContent = label;
    if (val === settings.dialect) opt.selected = true;
    dialectSelect.append(opt);
  }
  dialectSelect.addEventListener('change', () => {
    settings.dialect = dialectSelect.value as Settings['dialect'];
    void saveSettings(settings);
  });
  dialectField.append(dialectSelect);
  wrap.append(dialectField);

  sec.append(wrap);
  return sec;
}

function renderDailyLimitsSection(settings: Settings): HTMLElement {
  const sec = section('Limites quotidiennes');
  const note = document.createElement('p');
  note.className = 'text-xs text-[color:var(--color-ink-muted)] mb-3';
  note.textContent = 'Nombre maximum de nouvelles cartes / phrases / paires par jour pour chaque piste.';
  sec.append(note);

  const wrap = document.createElement('div');
  wrap.className = 'grid grid-cols-2 gap-3';

  const limits: [keyof Pick<Settings, 'dailyVocabLimit' | 'dailyClozeLimit' | 'dailyHvptLimit' | 'dailyEcouteLimit'>, string][] = [
    ['dailyVocabLimit', 'Vocabulaire'],
    ['dailyClozeLimit', 'Grammaire (cloze)'],
    ['dailyHvptLimit', 'Prononciation (paires)'],
    ['dailyEcouteLimit', 'Écoute (documents)'],
  ];
  for (const [key, label] of limits) {
    const f = field(label, '');
    const input = document.createElement('input');
    input.type = 'number';
    input.min = '1';
    input.max = '200';
    input.value = String(settings[key]);
    input.className = inputClasses();
    input.addEventListener('change', () => {
      const v = Math.max(1, Math.min(200, parseInt(input.value, 10) || settings[key]));
      settings[key] = v;
      input.value = String(v);
      void saveSettings(settings);
    });
    f.append(input);
    wrap.append(f);
  }
  sec.append(wrap);
  return sec;
}

function renderAppearanceSection(settings: Settings): HTMLElement {
  const sec = section('Apparence');
  const wrap = document.createElement('div');
  wrap.className = 'flex items-center gap-3';

  for (const [val, label, icon] of [['auto', 'Auto', '🌓'], ['light', 'Clair', '☀️'], ['dark', 'Sombre', '🌙']] as const) {
    const btn = document.createElement('button');
    btn.className = 'flex-1 py-3 rounded-lg border text-sm font-medium transition-colors flex flex-col items-center gap-1 ' +
      (settings.theme === val
        ? 'border-[color:var(--color-fjord-600)] bg-[color:var(--color-fjord-50)] text-[color:var(--color-fjord-700)]'
        : 'border-[color:var(--color-border)] hover:bg-[color:var(--color-fjord-50)]');
    btn.innerHTML = `<span class="text-xl">${icon}</span><span>${label}</span>`;
    btn.addEventListener('click', () => {
      settings.theme = val;
      void saveSettings(settings).then(() => applyTheme(val));
      // Re-render section to update selection
      const newSec = renderAppearanceSection(settings);
      sec.replaceWith(newSec);
    });
    wrap.append(btn);
  }
  sec.append(wrap);
  return sec;
}

export function applyTheme(theme: Settings['theme']): void {
  document.documentElement.style.colorScheme =
    theme === 'auto' ? 'light dark' : theme;
}

function renderAudioSection(
  caps: { hasSpeech: boolean; voiceCount: number; voiceNames: string[]; neuralVoices: string[] },
  ttsCoverageCount: number,
  ttsVoiceCount: number,
): HTMLElement {
  const sec = section('Audio');
  const block = document.createElement('div');
  block.className = 'flex flex-col gap-3 text-sm';

  // 1) Real human audio (NTNU NoW)
  const human = document.createElement('p');
  human.innerHTML = `🎙️ <strong>Voix humaine (NTNU NoW)</strong> — ~328 mots A1–A2 du corpus LearnNoW. Priorité 1, lue automatiquement quand un enregistrement existe.`;
  block.append(human);

  // 2) Neural TTS (pre-generated at build time via edge-tts)
  const neural = document.createElement('div');
  neural.className = 'p-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]';
  if (ttsCoverageCount > 0) {
    neural.innerHTML = `
      🎤 <strong>Voix Neural pré-générées</strong> — ${ttsCoverageCount} cartes couvertes par ${ttsVoiceCount} voix (Finn, Pernille).
      Priorité 2, utilisées quand pas d'audio humain. Stockées localement, fonctionnent offline.
    `;
  } else {
    neural.innerHTML = `
      🎤 <strong>Voix Neural</strong> — non générées pour l'instant.<br>
      <span class="text-xs text-[color:var(--color-ink-muted)] inline-block mt-2">
        Pour générer Finn / Pernille (voix nb-NO Neural, gratuit, sans clé API) :
        <code class="bg-[color:var(--color-fjord-50)] px-1 py-0.5 rounded text-[11px]">npm run build:tts</code>
        (nécessite Python + <code class="bg-[color:var(--color-fjord-50)] px-1 py-0.5 rounded text-[11px]">pip install edge-tts</code>)
      </span>
    `;
  }
  block.append(neural);

  // 3) Live TTS fallback
  const tts = document.createElement('p');
  tts.className = 'text-xs text-[color:var(--color-ink-muted)]';
  if (!caps.hasSpeech) {
    tts.innerHTML = '⚠️ Synthèse vocale Web Speech indisponible dans ce navigateur.';
  } else if (caps.voiceCount === 0) {
    tts.innerHTML = '🔉 <strong>Repli</strong> : aucune voix nb-NO système détectée. La synthèse live utilisera la voix système par défaut.';
  } else {
    const neuralNote = caps.neuralVoices.length > 0
      ? ` (dont ${caps.neuralVoices.length} neural, priorisée${caps.neuralVoices.length > 1 ? 's' : ''})`
      : '';
    tts.innerHTML = `🔉 <strong>Repli (priorité 3)</strong> : ${caps.voiceCount} voix nb-NO système${neuralNote} — ${caps.voiceNames.slice(0, 2).join(', ')}${caps.voiceNames.length > 2 ? '…' : ''}.`;
  }
  block.append(tts);

  // 3) Siddis honesty
  const siddis = document.createElement('p');
  siddis.className = 'p-3 rounded-lg bg-[color:var(--color-stavanger)]/20 border border-[color:var(--color-stavanger)] text-xs';
  siddis.innerHTML = '📍 <strong>Siddis (Stavanger)</strong> — aucun corpus audio libre n\'existe pour ce dialecte urbain. Le texte est affiché en siddis (kordan, eg, kor, ka, ikkje), mais la lecture audio reste en bokmål standard. Pour entendre du siddis réel : NRK Rogaland, ou un locuteur natif.';
  block.append(siddis);

  sec.append(block);
  return sec;
}

function renderDataSection(): HTMLElement {
  const sec = section('Données');
  const wrap = document.createElement('div');
  wrap.className = 'flex flex-col gap-3';

  const exportBtn = document.createElement('button');
  exportBtn.className = 'self-start px-4 py-2 rounded-lg border border-[color:var(--color-border)] text-sm hover:bg-[color:var(--color-fjord-50)] transition-colors';
  exportBtn.textContent = '📥 Exporter mes données (JSON)';
  exportBtn.addEventListener('click', async () => {
    const data = await exportAllData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parlenorvegien-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  });

  const resetBtn = document.createElement('button');
  resetBtn.className = 'self-start px-4 py-2 rounded-lg border border-[color:var(--color-rorbu)] text-sm text-[color:var(--color-rorbu)] hover:bg-[color:var(--color-rorbu)]/10 transition-colors';
  resetBtn.textContent = '🗑️ Réinitialiser toutes mes données';
  resetBtn.addEventListener('click', async () => {
    if (!confirm('Cette action effacera vos cartes FSRS, votre progression, vos paramètres et vos tâches enregistrées. Continuer ?')) return;
    await resetAllData();
    location.reload();
  });

  wrap.append(exportBtn, resetBtn);
  sec.append(wrap);
  return sec;
}

// --- helpers ---

function field(label: string, hint: string): HTMLElement {
  const f = document.createElement('label');
  f.className = 'flex flex-col gap-1';
  f.innerHTML = `
    <span class="text-sm font-medium">${label}</span>
    ${hint ? `<span class="text-xs text-[color:var(--color-ink-muted)]">${hint}</span>` : ''}
  `;
  return f;
}

function inputClasses(): string {
  return 'px-3 py-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-sm';
}
